
import { AfterViewInit, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { AppSetting } from '../../../models/app-setting.models';
import { PermissionNames } from '../../../models/permission-names.const';
import { AppSettingService } from '../../../services/app-setting.service';
import { AuthService } from '../../../services/auth.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { AppSettingEditDialogComponent } from './app-setting-edit-dialog/app-setting-edit-dialog.component';

@Component({
  selector: 'app-app-settings',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent
],
  templateUrl: './app-settings.component.html',
  styleUrls: ['./app-settings.component.scss'],
})
export class AppSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  private appSettingService = inject(AppSettingService);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);

  private destroy$ = new Subject<void>();

  // Data properties
  settings: AppSetting[] = [];
  filteredSettings: AppSetting[] = [];
  dataSource = new MatTableDataSource<AppSetting>();
  readonly sort = viewChild(MatSort);
  isLoading = false;
  error: string | null = null;
  sortField = 'key';
  sortDirection: 'asc' | 'desc' = 'asc';
  canEditSettings = false;

  // Search
  searchForm: FormGroup;
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Table configuration
  displayedColumns: string[] = ['dataType', 'key', 'value', 'isActive'];

  constructor() {
    this.searchForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadSettings();
    this.checkPermissions();
  }

  /**
   * Check if current user can edit settings
   */
  private checkPermissions(): void {
    this.canEditSettings = this.authService.hasNamedPermission(
      PermissionNames.Admin.AppSettings.Write,
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort();
  }

  /**
   * Load all application settings
   */
  loadSettings(): void {
    this.isLoading = true;
    this.error = null;

    this.appSettingService
      .getAllSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.settings = settings;
          this.applyFilter(this.searchQuery);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading app settings:', error);
          this.error = 'Failed to load application settings. Please try again.';
          this.isLoading = false;
          this.snackbarService.showError(
            'Failed to load application settings',
          );
        },
      });
  }

  /**
   * Get data type badge color
   */
  getDataTypeBadgeColor(dataType: string): string {
    const typeMap: { [key: string]: string } = {
      String: 'primary',
      Int: 'accent',
      Bool: 'warn',
      Url: 'accent',
      Json: 'accent',
    };
    return typeMap[dataType] || 'primary';
  }

  /**
   * Retry loading after error
   */
  retryLoad(): void {
    this.loadSettings();
  }

  /**
   * Apply search filter to settings
   */
  private applyFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredSettings = [...this.settings];
    } else {
      this.filteredSettings = this.settings.filter(
        (setting) =>
          setting.key.toLowerCase().includes(term) ||
          setting.value.toLowerCase().includes(term) ||
          (setting.description &&
            setting.description.toLowerCase().includes(term)) ||
          setting.dataType.toLowerCase().includes(term),
      );
    }

    this.applySortToFiltered();
    this.dataSource.data = this.filteredSettings;
  }

  /**
   * Apply current sort to filtered settings
   */
  private applySortToFiltered(): void {
    this.filteredSettings.sort((a, b) => {
      const aValue = this.getSortValue(a, this.sortField);
      const bValue = this.getSortValue(b, this.sortField);

      if (aValue < bValue) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  /**
   * Handle sort change from table
   */
  sortSettings(sort: Sort): void {
    this.sortField = sort.active;
    this.sortDirection = sort.direction as 'asc' | 'desc';

    this.applySortToFiltered();
    this.dataSource.data = this.filteredSettings;
  }

  /**
   * Get sort value for a given field
   */
  private getSortValue(setting: AppSetting, field: string): any {
    switch (field) {
      case 'key':
        return setting.key.toLowerCase();
      case 'value':
        return setting.value.toLowerCase();
      case 'dataType':
        return setting.dataType.toLowerCase();
      case 'isActive':
        return setting.isActive ? 1 : 0;
      default:
        return '';
    }
  }

  /**
   * Handle search input with debouncing
   */
  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applyFilter(this.searchQuery);
    }, 300);
  }

  /**
   * Clear search filter
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter('');
  }

  /**
   * Open edit dialog for a setting
   */
  onSettingClick(setting: AppSetting): void {
    if (!this.canEditSettings) {
      return;
    }

    const dialogRef = this.dialog.open(AppSettingEditDialogComponent, {
      width: '600px',
      data: { setting: { ...setting } },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Reload settings to get updated data
        this.loadSettings();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
